import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";

export type ConfirmationDialogType =
  | "warning"
  | "error"
  | "info"
  | "success"
  | "question";

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type?: ConfirmationDialogType;
  confirmText?: string;
  cancelText?: string;
  confirmColor?:
    | "primary"
    | "secondary"
    | "error"
    | "warning"
    | "info"
    | "success";
  showIcon?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  type = "question",
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "primary",
  showIcon = true,
  maxWidth = "xs",
}) => {
  const getTitleColor = () => {
    switch (type) {
      case "warning":
        return "#ff9800";
      case "error":
        return "#f44336";
      case "info":
        return "#1a237e";
      case "success":
        return "#4caf50";
      case "question":
      default:
        return "#1a237e";
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      TransitionProps={{
        timeout: 300,
      }}
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          minHeight: 240,
          overflow: "visible",
          background:
            "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,250,252,1) 100%)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                color: getTitleColor(),
                fontWeight: 700,
                letterSpacing: "-0.5px",
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                // width: 40,
                height: 3,
                background: `linear-gradient(90deg, ${getTitleColor()} 0%, transparent 100%)`,
                borderRadius: 2,
              }}
            />
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "text.secondary",
              mt: -0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "rotate(90deg)",
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 3 }}>
        <DialogContentText
          sx={{
            fontSize: "1rem",
            color: "text.primary",
            lineHeight: 1.7,
            fontWeight: 400,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          pt: 1,
          gap: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          fullWidth
          sx={{
            py: 1.25,
            fontWeight: 600,
            fontSize: "0.9375rem",
            textTransform: "none",
            borderRadius: 2,
            borderWidth: "1.5px",
            color: "text.secondary",
            borderColor: "rgba(0,0,0,0.15)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              borderWidth: "1.5px",
              borderColor: "rgba(0,0,0,0.3)",
              backgroundColor: "rgba(0,0,0,0.04)",
              transform: "translateY(-1px)",
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmColor}
          fullWidth
          autoFocus
          sx={{
            py: 1.25,
            fontWeight: 600,
            fontSize: "0.9375rem",
            textTransform: "none",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
